import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiMessageCircle,
  FiCamera,
  FiSend,
  FiCheckCircle,
  FiXCircle,
  FiZap,
  FiClock,
  FiMapPin,
  FiTruck,
  FiPhone,
  FiUser,
  FiPlay,
  FiFlag,
} from "react-icons/fi";
import { MissionBadges } from "./components/MissionCard";
import MissionTypeIcon from "./components/MissionTypeIcon";
import { Phone as PhoneIcon } from "lucide-react";
import {
  getMission,
  listMissionProposals,
  createProposal,
  acceptProposal,
  refuseProposal,
  setMissionStatus,
  confirmMissionPresence,
  getProviderSnapshot,
  typeLabel,
  formatPrice,
  formatDate,
  formatDateTime,
  AVAILABILITIES,
  STATUS_LABELS,
  STATUS_TONES,
  assetUrl,
} from "../../utils/missionsApi";

function QuickApplyPanel({ mission, onSubmitted, onCancel }) {
  const [snapshot, setSnapshot] = useState(null);
  const [price, setPrice] = useState(mission?.estimatedBudget ? String(mission.estimatedBudget) : "");
  const [estimatedDelay, setDelay] = useState("");
  const [availability, setAvailability] = useState("disponible_semaine");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const COMMISSION = 0.10;

  useEffect(() => {
    getProviderSnapshot().then(setSnapshot).catch(() => setSnapshot(null));
  }, []);

  const providerAmount = useMemo(() => {
    const n = Number(price) || 0;
    return Math.max(0, Math.round(n * (1 - COMMISSION) * 100) / 100);
  }, [price]);

  const submit = async (e) => {
    e.preventDefault();
    if (!price || Number(price) <= 0) return alert("Indiquez un prix valide.");
    setSubmitting(true);
    try {
      await createProposal(mission._id, {
        price: Number(price),
        estimatedDelay,
        availability,
        message,
      });
      alert("✅ Votre candidature a été envoyée !");
      onSubmitted();
    } catch (err) {
      alert(err?.response?.data?.message || "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="pmm-card pmm-form" onSubmit={submit}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Postuler en 30 secondes</h2>
        <button type="button" className="pmm-btn" onClick={onCancel}>Annuler</button>
      </div>

      {snapshot && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13, color: "var(--text-muted)" }}>
          <span className="pmm-chip pmm-chip--brand"><FiUser /> {snapshot.name}</span>
          {snapshot.specialty && <span className="pmm-chip pmm-chip--neutral">{snapshot.specialty}</span>}
          {snapshot.city && <span className="pmm-chip pmm-chip--neutral"><FiMapPin /> {snapshot.city}</span>}
          {snapshot.phone && <span className="pmm-chip pmm-chip--neutral"><FiPhone /> {snapshot.phone}</span>}
          {snapshot.completedMissions > 0 && (
            <span className="pmm-chip pmm-chip--success">{snapshot.completedMissions} missions</span>
          )}
        </div>
      )}

      <div className="pmm-row pmm-row--3">
        <div className="pmm-field">
          <label>Votre prix (€) *</label>
          <input
            type="number" min={1} value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="450"
            required
          />
        </div>
        <div className="pmm-field">
          <label>Délai estimé</label>
          <input
            type="text" value={estimatedDelay}
            onChange={(e) => setDelay(e.target.value)}
            placeholder="Sous 24h, mardi matin..."
          />
        </div>
        <div className="pmm-field">
          <label>Disponibilité</label>
          <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
            {AVAILABILITIES.map((a) => (
              <option key={a.key} value={a.key}>{a.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="pmm-field">
        <label>Message (optionnel)</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Expliquez brièvement votre approche, disponibilité, garantie..."
        />
      </div>

      {Number(price) > 0 && (
        <div className="pmm-commission">
          <div className="pmm-commission-row">
            <span>Montant facturé</span><b>{formatPrice(price)}</b>
          </div>
          <div className="pmm-commission-row pmm-commission-row--muted">
            <span>Commission plateforme (10 %)</span><span>− {formatPrice(Number(price) * COMMISSION)}</span>
          </div>
          <div className="pmm-commission-row pmm-commission-row--total">
            <span>Vous percevez</span><span>{formatPrice(providerAmount)}</span>
          </div>
          <div className="pmm-commission-info">
            <FiCheckCircle size={11} /> Paiement sécurisé via la plateforme à venir.
          </div>
        </div>
      )}

      <div className="pmm-sticky-actions">
        <button type="submit" className="pmm-btn pmm-btn--primary pmm-btn--block" disabled={submitting}>
          {submitting ? "Envoi…" : (<><FiSend /> Envoyer ma proposition</>)}
        </button>
      </div>
    </form>
  );
}

export default function MissionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const userId = localStorage.getItem("userId") || "";
  const isProvider = role === "partner" || role === "admin";

  const [mission, setMission] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(params.get("apply") === "1");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const m = await getMission(id);
      setMission(m);
      const { items } = await listMissionProposals(id);
      setProposals(items || []);
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur lors du chargement.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isOwner = mission && (String(mission.createdBy?._id || mission.createdBy) === userId);
  const isAssigned = mission && mission.assignedTo && String(mission.assignedTo._id || mission.assignedTo) === userId;
  const alreadyApplied = proposals.some((p) => String(p.providerId?._id || p.providerId) === userId);

  if (loading) {
    return (
      <div className="pmm-card">
        <div className="pmm-spinner" />
      </div>
    );
  }
  if (!mission) {
    return (
      <div className="pmm-empty">
        <div className="pmm-empty-icon">❓</div>
        <h3>Mission introuvable</h3>
        <p>Cette mission n'existe plus ou vous n'avez pas accès.</p>
      </div>
    );
  }

  const handleAccept = async (proposalId) => {
    if (!window.confirm("Accepter cette proposition ?")) return;
    try {
      await acceptProposal(proposalId);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur");
    }
  };
  const handleRefuse = async (proposalId) => {
    if (!window.confirm("Refuser cette proposition ?")) return;
    try {
      await refuseProposal(proposalId);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur");
    }
  };

  const handleStatus = async (status) => {
    if (!window.confirm(`Changer le statut vers "${STATUS_LABELS[status] || status}" ?`)) return;
    try {
      await setMissionStatus(mission._id, status);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur");
    }
  };

  const handleConfirmPresence = async () => {
    if (!window.confirm("Confirmer votre présence sur cette mission ?")) return;
    try {
      await confirmMissionPresence(mission._id);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur");
    }
  };

  const tone = STATUS_TONES[mission.status] || "neutral";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="pmm-topbar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="pmm-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Retour</button>
          <MissionTypeIcon type={mission.type} size={48} />
          <div>
            <h1 style={{ fontSize: "clamp(1.3rem, 2.4vw, 1.7rem)" }}>{mission.title}</h1>
            <div className="pmm-topbar-sub">{typeLabel(mission.type)} • {mission.city || "—"}</div>
          </div>
        </div>
        <div className="pmm-topbar-actions">
          <span className={`pmm-chip pmm-chip--${tone}`}>{STATUS_LABELS[mission.status] || mission.status}</span>
        </div>
      </div>

      <div className="pmm-card">
        <MissionBadges mission={mission} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 14 }}>
          <Info icon={<FiMapPin />} label="Ville" value={mission.city || "—"} />
          <Info icon={<FiClock />} label="Date" value={formatDate(mission.date)} />
          <Info icon={<FiZap />} label="Urgence" value={mission.isUrgent ? "Urgent" : "Normale"} />
          <Info icon={<FiTruck />} label="Budget" value={formatPrice(mission.estimatedBudget || 0)} />
        </div>

        {mission.description && (
          <div style={{ marginTop: 16, color: "var(--text-secondary)", fontSize: 15, whiteSpace: "pre-line" }}>
            {mission.description}
          </div>
        )}

        {(mission.vehicleBrand || mission.vehicleModel || mission.vehiclePlate) && (
          <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {mission.vehicleBrand && <span className="pmm-chip pmm-chip--neutral">🚗 {mission.vehicleBrand}</span>}
            {mission.vehicleModel && <span className="pmm-chip pmm-chip--neutral">{mission.vehicleModel}</span>}
            {mission.vehiclePlate && <span className="pmm-chip pmm-chip--neutral">{mission.vehiclePlate}</span>}
          </div>
        )}

        {mission.type === "convoyage" && (
          <div className="pmm-card" style={{ background: "var(--cream-warm)", marginTop: 16 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>🚛 Convoyage</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              <Info icon={<FiMapPin />} label="Départ" value={mission.departureCity || "—"} />
              <Info icon={<FiMapPin />} label="Arrivée" value={mission.arrivalCity || "—"} />
              <Info icon={<FiClock />} label="Date départ" value={formatDateTime(mission.departureDate)} />
              <Info icon={<FiClock />} label="Date arrivée" value={formatDateTime(mission.arrivalDate)} />
              {mission.fuelLevel && <Info label="Carburant" value={mission.fuelLevel} />}
              {mission.departureMileage > 0 && <Info label="km départ" value={mission.departureMileage} />}
              {mission.arrivalMileage > 0 && <Info label="km arrivée" value={mission.arrivalMileage} />}
            </div>
          </div>
        )}

        {mission.specialInstructions && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Instructions spéciales</h3>
            <div style={{ color: "var(--text-secondary)", whiteSpace: "pre-line" }}>
              {mission.specialInstructions}
            </div>
          </div>
        )}

        {(mission.photos || []).length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Photos</h3>
            <div className="pmm-grid pmm-grid--4">
              {(mission.photos || []).map((p, i) => (
                <a key={i} href={assetUrl(p.url)} target="_blank" rel="noreferrer">
                  <img src={assetUrl(p.url)} alt="" style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 12, border: "1px solid var(--border)" }} />
                </a>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 18 }}>
          {(isOwner || isAssigned) && (
            <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate(`/missions/${mission._id}/messages`)}>
              <FiMessageCircle /> Messages
            </button>
          )}
          {(isOwner || isAssigned) && (
            <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate(`/missions/${mission._id}/photos`)}>
              <FiCamera /> Photos / Validation
            </button>
          )}

          {/* Provider actions */}
          {isProvider && !isOwner && !alreadyApplied && ["ouverte", "propositions_recues"].includes(mission.status) && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => { setShowApply(true); setParams({ apply: "1" }, { replace: true }); }}>
              <FiSend /> Postuler
            </button>
          )}
          {isProvider && alreadyApplied && (
            <span className="pmm-chip pmm-chip--success"><FiCheckCircle /> Candidature envoyée</span>
          )}

          {/* Assigned provider workflow */}
          {isAssigned && mission.status === "proposition_acceptee" && (
            <button className="pmm-btn pmm-btn--primary" onClick={handleConfirmPresence}>
              <FiCheckCircle /> Confirmer ma présence
            </button>
          )}
          {isAssigned && mission.status === "presence_confirmee" && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => handleStatus("en_cours")}>
              <FiPlay /> Démarrer la mission
            </button>
          )}
          {isAssigned && mission.status === "en_cours" && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => handleStatus("terminee")}>
              <FiFlag /> Marquer terminée
            </button>
          )}

          {/* Owner workflow */}
          {isOwner && mission.status === "terminee" && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => handleStatus("validee")}>
              <FiCheckCircle /> Valider le travail
            </button>
          )}
          {isOwner && ["ouverte", "propositions_recues", "proposition_acceptee"].includes(mission.status) && (
            <button className="pmm-btn pmm-btn--danger" onClick={() => handleStatus("annulee")}>
              <FiXCircle /> Annuler
            </button>
          )}
        </div>
      </div>

      {showApply && isProvider && !isOwner && !alreadyApplied && (
        <QuickApplyPanel
          mission={mission}
          onSubmitted={() => { setShowApply(false); setParams({}, { replace: true }); load(); }}
          onCancel={() => { setShowApply(false); setParams({}, { replace: true }); }}
        />
      )}

      {/* Proposals */}
      {role === "admin" && (
        <div className="pmm-card">
          <h2 style={{ margin: "0 0 12px", fontSize: 18 }}>
            Propositions reçues ({proposals.length})
          </h2>
          {proposals.length === 0 ? (
            <div className="pmm-empty">
              <div className="pmm-empty-icon">📭</div>
              <h3>Aucune proposition pour le moment</h3>
              <p>Les prestataires intéressés posteront leur offre ici.</p>
            </div>
          ) : (
            <div className="pmm-grid pmm-grid--2">
              {proposals.map((p) => (
                <div key={p._id} className="pmm-card" style={{ background: "var(--cream-warm)", padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div>
                      <strong>{p.providerSnapshot?.name || "Prestataire"}</strong>
                      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                        {p.providerSnapshot?.specialty || "—"} • {p.providerSnapshot?.city || "—"}
                      </div>
                    </div>
                    <span className={`pmm-chip pmm-chip--${
                      p.status === "acceptee" ? "success"
                      : p.status === "refusee" ? "danger"
                      : p.status === "envoyee" ? "info" : "neutral"
                    }`}>{p.status}</span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginTop: 12 }}>
                    <Info label="Prix" value={formatPrice(p.price)} />
                    <Info label="Délai" value={p.estimatedDelay || "—"} />
                    <Info label="Provider reçoit" value={formatPrice(p.providerAmount)} />
                  </div>

                  {p.message && (
                    <div style={{ marginTop: 10, fontSize: 13, color: "var(--text-secondary)", whiteSpace: "pre-line" }}>
                      “{p.message}”
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    {p.providerSnapshot?.phone && (
                      <a className="pmm-btn pmm-btn--ghost" href={`tel:${p.providerSnapshot.phone}`}>
                        <PhoneIcon size={14} /> Appeler
                      </a>
                    )}
                    {!mission.assignedTo && ["envoyee", "vue"].includes(p.status) && (
                      <>
                        <button className="pmm-btn pmm-btn--primary" onClick={() => handleAccept(p._id)}>
                          <FiCheckCircle /> Accepter
                        </button>
                        <button className="pmm-btn pmm-btn--danger" onClick={() => handleRefuse(p._id)}>
                          <FiXCircle /> Refuser
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ icon, label, value }) {
  return (
    <div className="pmm-info-card">
      <span className="pmm-info-card__label">
        {icon} {label}
      </span>
      <span className="pmm-info-card__value">{value ?? "—"}</span>
    </div>
  );
}
